import React, { useState, useEffect } from 'react';
import vector from '../assets/images/Vector.svg';
import { getMetaDataCall, getUserEmailId, UpdateUserCall } from "lib/axiosUtils";
import Dropdown from './Dropdown';
import { TYPE_SUCCESS, INTERVAL } from "assets/constants/Constants";
import ShowToast from 'components/ShowToast';

const COMPANY_SIZE_OPTIONS = [
    { label: '1–50 employees', value: '1-50' },
    { label: '51–250 employees', value: '51-250' },
    { label: '251–1,000 employees', value: '251-1000' },
    { label: '1,001+ employees', value: '1000+' },
];

export default function PersonalizeModal({ isOpen, onClose, onSubmit, userEmail }) {
    const [personalizeData, setPersonalizeData] = useState({
        country: '',
        role: '',
        designation: '',
        companySize: ''
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Dropdown option lists, populated from the metadata API
    const [countriesList, setCountriesList] = useState([]);
    const [designationList, setDesignationList] = useState([]);
    const [primaryRoleList, setPrimaryRoleList] = useState([]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setIsLoading(true);
                const metadata = await getMetaDataCall({}, {});
                const metaData = metadata?.data?.result?.[0]?.meta_data;

                if (metaData) {
                    const { countries = [], designation = [], primaryrole = [] } = metaData;

                    setCountriesList(
                        countries.map((item) => ({
                            label: item.country_name,
                            value: item.country_id,
                        }))
                    );

                    setDesignationList(
                        designation.map((item) => ({
                            label: item.designation_name,
                            value: item.designation_name,
                        }))
                    );

                    setPrimaryRoleList(
                        primaryrole.map((item) => ({
                            label: item.primaryrole_name,
                            value: item.primaryrole_name,
                        }))
                    );
                }
            } catch (err) {
                console.error("Error loading signup metadata:", err);
                setError({ message: "Failed to load form options. Please try again." });
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchInitialData();
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!userEmail) {
            setError({ message: 'Missing user reference. Please restart signup and try again.' });
            return;
        }

        setIsSubmitting(true);

        try {
            // Resolve the user's _id from their email first.
            const userFound = await getUserEmailId({}, {}, userEmail);
            const userId = userFound?.data?.result?.[0]?._id;

            if (!userId) {
                throw new Error('We could not find your account. Please restart signup and try again.');
            }

            // NOTE: adjust these key names to whatever your backend's
            // update-user endpoint actually expects — matched here to the
            // createUserCall payload's naming convention (userXxx).
            const requestData = {
                userCountry: personalizeData.country,
                userPrimaryRole: personalizeData.role,
                userDesignation: personalizeData.designation,
                userCompanySize: personalizeData.companySize,
            };

            const userResponse = await UpdateUserCall({}, requestData, userId);

            if (userResponse?.statusCode === 200) {
                ShowToast(
                    'Your Personal preferences saved successfully. Please Login.',
                    TYPE_SUCCESS,
                    INTERVAL,
                    "",
                    ""
                );
                onClose();
            } else {
                throw new Error(userResponse?.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            const message = err.message || 'Something went wrong. Please try again.';
            setError({ message });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <section className="signup-page">
            <div className="signup-card signup-card--personalize">
                <div className="signup-panel-right signup-panel-right--personalize" style={{ position: 'relative' }}>

                    {/* Close button top-right */}
                    <button
                        type="button"
                        className="personalize-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        &times;
                    </button>

                    <div className="personalize-form-wrapper">
                        <h3 className="personalize-form__title">
                            Help us personalize<br />your experience
                        </h3>
                        <p className="personalize-form__subtitle">
                            This helps us configure the right recommendations.
                        </p>

                        {error && (
                            <p className="personalize-form__error" role="alert">
                                {error.message}
                            </p>
                        )}

                        <form className="personalize-form" onSubmit={handleSubmit} noValidate>
                            {/* Country */}
                            <div className="form-field">
                                <label className="form-field__label">Country</label>
                                <Dropdown
                                    options={countriesList}
                                    value={personalizeData.country}
                                    onChange={(val) => setPersonalizeData((prev) => ({ ...prev, country: val }))}
                                    placeholder={isLoading ? 'Loading countries...' : 'Select your country'}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Primary function / role */}
                            <div className="form-field">
                                <label className="form-field__label">Select your primary function</label>
                                <Dropdown
                                    options={primaryRoleList}
                                    value={personalizeData.role}
                                    onChange={(val) => setPersonalizeData((prev) => ({ ...prev, role: val }))}
                                    placeholder={isLoading ? 'Loading...' : 'Select your primary function'}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Seniority / designation */}
                            <div className="form-field">
                                <label className="form-field__label">Select your seniority</label>
                                <Dropdown
                                    options={designationList}
                                    value={personalizeData.designation}
                                    onChange={(val) => setPersonalizeData((prev) => ({ ...prev, designation: val }))}
                                    placeholder={isLoading ? 'Loading...' : 'Select your seniority'}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Company size - not in metadata, kept static */}
                            <div className="form-field">
                                <label className="form-field__label">Select your organisation size</label>
                                <Dropdown
                                    options={COMPANY_SIZE_OPTIONS}
                                    value={personalizeData.companySize}
                                    onChange={(val) => setPersonalizeData((prev) => ({ ...prev, companySize: val }))}
                                    placeholder="Select your organisation size"
                                />
                            </div>

                            <div className="personalize-form__actions">
                                <button
                                    type="submit"
                                    className="personalize-form__submit"
                                    disabled={isLoading || isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                    <img src={vector} className="cta-button__icon" alt="" />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}